import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import { Button, Spinner, Input, InfoBox } from '@crazyegginc/hatch';

import { funnelsUsingGoalsQuery } from '/src/features/funnels/queries';
import { useModal } from '/src/hooks';
import { Modal } from '/src/contexts/modal';
import { inflect } from '/src/utils/string';
import { GOAL_TRIGGER_TYPES } from '../../constants';

const validationSchema = yup.object().shape({
  delete: yup
    .string()
    .required('required')
    .matches(/^DELETE$/, 'must match'),
});

export function DeleteModal({ goal, isDraft = false, onConfirm = null, onCancel = null, isDeleting = false }) {
  const modal = useModal();
  const hasApiTrigger = goal?.triggers?.some?.((t) => t.type === GOAL_TRIGGER_TYPES.API) ?? false;

  const { data, isFetching } = useQuery({
    ...funnelsUsingGoalsQuery({ siteId: goal.siteId, goalIds: [goal.id] }),
    enabled: Boolean(goal?.id),
  });

  if (isFetching) {
    return (
      <Modal dialogClassName="!w-[640px]">
        <Modal.Title>Delete this {isDraft ? 'draft ' : ''}Conversion?</Modal.Title>
        <div className="my-5 flex w-full items-center justify-center">
          <Spinner />
          <div className="ml-2.5">Loading...</div>
        </div>
      </Modal>
    );
  }

  const funnels = data?.funnelsUsingGoals ?? [];

  return (
    <Modal dialogClassName="!w-[640px]">
      <Modal.Title>Delete this {isDraft ? 'draft ' : ''}Conversion?</Modal.Title>
      <div className="mb-6 flex w-full justify-between">
        <div className="text-body-2 mt-0 flex flex-col space-y-2.5 text-sm leading-5">
          {goal.abTestsCountActive > 0 || funnels.length > 0 ? (
            <>
              <span>If you delete this Conversion, you will also delete its associated:</span>
              <span>
                {goal.abTestsCountActive > 0 ? (
                  <>
                    <span className="text-body-1">
                      {goal.abTestsCountActive} A/B {inflect('Test', goal.abTestsCountActive)}:
                    </span>
                    <span>
                      <a
                        href={`/goals/${goal.id}#ab-tests`}
                        className="text-link ml-5"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        See the affected A/B {inflect('Test', goal.abTestsCountActive)} here.
                      </a>
                    </span>
                  </>
                ) : null}
              </span>
              <span>
                {funnels.length > 0 ? (
                  <>
                    <span className="text-body-1">
                      {funnels.length} {inflect('Funnel', funnels.length)}:
                    </span>
                    <ul className="ml-10 list-outside list-disc mb-2">
                      {funnels.map((f) => (
                        <li key={f.id}>
                          <a
                            href={`/funnels/edit/${f.id}`}
                            className="text-link"
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            {f.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </span>
            </>
          ) : (
            <span>Are you sure you want to delete this {isDraft ? 'draft' : 'saved'} Conversion?</span>
          )}

          <span className="text-body-1">Deleting this Conversion cannot be undone.</span>
        </div>
      </div>
      <Formik
        initialValues={{ delete: '' }}
        validationSchema={validationSchema}
        onSubmit={() => {
          onConfirm?.(goal.id);
          modal.close();
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldTouched }) => (
          <Form>
            <div className="text-body-2 leading-normal mb-2.5">
              <Input
                name="delete"
                id="delete-confirm-input"
                label="Type DELETE below to confirm:"
                placeholder="Type DELETE to continue"
                onFocus={() => setFieldTouched('delete', true, true)}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.delete}
                autoComplete="off"
              />
              <span className="text-caption">case sensitive</span>
            </div>

            {hasApiTrigger ? (
              <InfoBox
                body={
                  <span>
                    {' '}
                    Because this Conversion includes trigger(s) called via our API, to delete it please make sure to not
                    send us any more data via the API.{' '}
                    <strong> If you keep sending us data via the API the Conversion will be created again.</strong>
                  </span>
                }
              />
            ) : null}

            <Modal.Actions>
              <Button variant="warning" disabled={!!errors.delete || !touched.delete || isDeleting} type="submit">
                {isDeleting ? (
                  <>
                    <Spinner />
                    <span>Deleting...</span>
                  </>
                ) : (
                  `Delete ${isDraft ? 'draft' : 'Conversion'}${
                    goal.abTestsCountActive > 0 ? ' & A/B ' + inflect('Test', goal.abTestsCountActive) : ''
                  }${funnels.length > 0 ? ' & ' + inflect('Funnel', funnels.length) : ''}`
                )}
              </Button>
              <Button
                variant="cancel"
                disabled={isDeleting}
                onClick={() => {
                  onCancel?.();
                  modal.close();
                }}
              >
                Cancel
              </Button>
            </Modal.Actions>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
